<template>
  <transition v-bind="modalTransition">
    <div v-if="isOpen" class="fixed inset-0 z-9999 flex items-center justify-center bg-black bg-opacity-50">
      <div class="mx-auto w-full max-w-lg bg-white p-6 shadow-4 max-h-[90vh] overflow-y-auto">
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-xl font-semibold">{{ isEdit ? 'Editar Usuário' : 'Novo Usuário' }}</h2>
          <button @click="cancel" class="hover:text-slate-400">
            <IcClose />
          </button>
        </div>

        <div v-if="isFetchingUser" class="flex justify-center py-8">
          <svg class="h-8 w-8 animate-spin text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>

        <Form v-else :validation-schema="schema" :initial-values="initialValues" @submit="onSubmit" v-slot="{ values }">
          <div class="space-y-4">
            <!-- Nome -->
            <div>
              <label class="mb-2 block text-sm font-medium">Nome *</label>
              <Field name="name" v-slot="{ field, errorMessage }">
                <input
                  v-bind="field"
                  type="text"
                  class="w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none"
                  placeholder="Nome do usuário" />
                <p v-if="errorMessage" class="mt-1 text-xs text-red">{{ errorMessage }}</p>
              </Field>
            </div>

            <!-- Email -->
            <div>
              <label class="mb-2 block text-sm font-medium">Email *</label>
              <Field name="email" v-slot="{ field, errorMessage }">
                <input
                  v-bind="field"
                  type="email"
                  class="w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none"
                  placeholder="Email do usuário" />
                <p v-if="errorMessage" class="mt-1 text-xs text-red">{{ errorMessage }}</p>
              </Field>
            </div>

            <!-- Telefone -->
            <div>
              <label class="mb-2 block text-sm font-medium">Telefone *</label>
              <Field name="phone" v-slot="{ field, errorMessage }">
                <input
                  v-bind="field"
                  type="text"
                  class="w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none"
                  placeholder="Telefone" />
                <p v-if="errorMessage" class="mt-1 text-xs text-red">{{ errorMessage }}</p>
              </Field>
            </div>

            <!-- Nível de Acesso -->
            <div v-show="canEditRole">
              <label class="mb-2 block text-sm font-medium">Nível de Acesso *</label>
              <Field name="role" v-slot="{ field, errorMessage }">
                <select
                  v-bind="field"
                  class="w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none bg-white">
                  <option value="" disabled>Selecione o nível de acesso</option>
                  <option v-for="role in roles" :key="role.value" :value="role.value">
                    {{ role.label }}
                  </option>
                </select>
                <p v-if="errorMessage" class="mt-1 text-xs text-red">{{ errorMessage }}</p>
              </Field>
            </div>

            <!-- Turno -->
            <div>
              <label class="mb-2 block text-sm font-medium">Turno</label>
              <Field name="shift_id" v-slot="{ field, errorMessage }">
                <select
                  v-bind="field"
                  class="w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none bg-white">
                  <option value="">Sem turno específico</option>
                  <option v-for="shift in shifts" :key="shift.id" :value="shift.id">
                    {{ shift.name }} ({{ formatTime(shift.start_time) }} - {{ formatTime(shift.end_time) }})
                  </option>
                </select>
                <p v-if="errorMessage" class="mt-1 text-xs text-red">{{ errorMessage }}</p>
              </Field>
            </div>

            <!-- Notificações -->
            <div class="mt-4 border-t border-stroke pt-4">
              <h3 class="mb-3 text-sm font-semibold text-slate-700">Notificações</h3>
              <div class="flex flex-col gap-3">
                <label class="flex items-center gap-2 cursor-pointer">
                  <Field name="receive_alerts" type="checkbox" :value="true" :unchecked-value="false" class="h-4 w-4 border-stroke text-primary focus:ring-primary" />
                  <span class="text-sm font-medium">Receber alertas do sistema (Geral)</span>
                </label>

                <div class="ml-6 flex flex-col gap-2" :class="{ 'opacity-50 pointer-events-none': !values.receive_alerts }">
                  <label class="flex items-center gap-2 cursor-pointer">
                    <Field name="receive_push" type="checkbox" :value="true" :unchecked-value="false" :disabled="!values.receive_alerts" class="h-4 w-4 border-stroke text-primary focus:ring-primary" />
                    <span class="text-sm">Push</span>
                  </label>
                  <label class="flex items-center gap-2 cursor-pointer">
                    <Field name="receive_whatsapp" type="checkbox" :value="true" :unchecked-value="false" :disabled="!values.receive_alerts" class="h-4 w-4 border-stroke text-primary focus:ring-primary" />
                    <span class="text-sm">WhatsApp</span>
                  </label>
                  <label class="flex items-center gap-2 cursor-pointer">
                    <Field name="receive_email" type="checkbox" :value="true" :unchecked-value="false" :disabled="!values.receive_alerts" class="h-4 w-4 border-stroke text-primary focus:ring-primary" />
                    <span class="text-sm">Email</span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Senhas apenas na criação -->
            <template v-if="!isEdit">

              <div>
                <label class="mb-2 block text-sm font-medium">Senha *</label>
                <Field name="password" v-slot="{ field, errorMessage }">
                  <input
                    v-bind="field"
                    type="password"
                    class="w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none"
                    placeholder="Senha" />
                  <p v-if="errorMessage" class="mt-1 text-xs text-red">{{ errorMessage }}</p>
                </Field>
              </div>

              <div>
                <label class="mb-2 block text-sm font-medium">Confirmar Senha *</label>
                <Field name="password_confirmation" v-slot="{ field, errorMessage }">
                  <input
                    v-bind="field"
                    type="password"
                    class="w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none"
                    placeholder="Confirme a senha" />
                  <p v-if="errorMessage" class="mt-1 text-xs text-red">{{ errorMessage }}</p>
                </Field>
              </div>
            </template>
          </div>

          <div class="mt-6 flex justify-end space-x-4">
            <button
              type="submit"
              :disabled="isLoading"
              class="bg-blue px-4 py-2 text-white shadow-sm hover:bg-blue-600 focus:outline-none disabled:opacity-50">
              <span v-if="isLoading">
                <ButtonLoader />
              </span>
              <span v-else>
                Salvar
              </span>
            </button>
            <button @click="cancel" type="button" class="text-slate-500 hover:text-slate-400" :disabled="isLoading">
              Cancelar
            </button>
          </div>
        </Form>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { Form, Field } from 'vee-validate'
import { modalTransition } from '@/plugins/transitions'
import IcClose from '@/components/Icons/IcClose.vue'
import ButtonLoader from '@/components/Globals/Feedbacks/ButtonLoader.vue'
import { useCompanyStore } from '@/store/company'
import { useUserStore } from '@/store/user'
import { useSuccessStore } from '@/store/success'
import { useI18n } from 'vue-i18n'
import { getCompanyUserSchema } from '@/schemas/CompanyUserSchema'
import { UserRoles, translateUserRole } from '@/interfaces/enums'
import UserResource from '@/api/resources/user'
import { ICreateUser } from '@/interfaces/entities/user'
import { IUpdateProfileParams } from '@/interfaces/api/user'
import { formatOutsideRegularUtcTimeToLocalHHmm } from '@/utils/date/date-time'

const props = defineProps<{
  isOpen: boolean
  userId?: number | null
}>()

const emit = defineEmits(['close', 'saved'])

const { t } = useI18n()
const companyStore = useCompanyStore()
const userStore = useUserStore()
const isLoading = ref(false)
const isFetchingUser = ref(false)

const shifts = computed(() => companyStore.shifts)

const canEditRole = computed(() => {
  const role = userStore.getCurrentUser?.role
  return role === UserRoles.ADMIN
})

const roles = [
  { value: UserRoles.ADMIN, label: translateUserRole(UserRoles.ADMIN) },
  { value: UserRoles.MANAGER, label: translateUserRole(UserRoles.MANAGER) },
  { value: UserRoles.TECHNICAL, label: translateUserRole(UserRoles.TECHNICAL) },
  { value: UserRoles.USER, label: translateUserRole(UserRoles.USER) },
]

function formatTime(timeStr?: string) {
  if (!timeStr) return ''
  return formatOutsideRegularUtcTimeToLocalHHmm(timeStr) || timeStr
}

const isEdit = computed(() => !!props.userId)
const schema = computed(() => getCompanyUserSchema(isEdit.value))

const initialValues = ref<any>({
  name: '',
  email: '',
  phone: '',
  role: UserRoles.TECHNICAL,
  shift_id: '',
  receive_alerts: false,
  receive_push: false,
  receive_email: false,
  receive_whatsapp: false,
  password: '',
  password_confirmation: '',
})

onMounted(() => {
  if (companyStore.shifts.length === 0) {
    companyStore.fetchCompanyShifts()
  }
})

watch(
  () => props.isOpen,
  async (isOpen) => {
    if (isOpen) {
      if (isEdit.value && props.userId) {
        await loadUser(props.userId)
      } else {
        initialValues.value = {
          name: '',
          email: '',
          phone: '',
          role: UserRoles.TECHNICAL,
          shift_id: '',
          receive_alerts: false,
          receive_push: false,
          receive_email: false,
          receive_whatsapp: false,
          password: '',
          password_confirmation: '',
        }
      }
    }
  }
)

async function loadUser(id: number) {
  isFetchingUser.value = true
  await nextTick()
  try {
    const userInStore = userStore.users.find(u => u.id === id)
    
    if (userInStore) {
      initialValues.value = {
        name: userInStore.name,
        email: userInStore.email,
        phone: userInStore.phone_number || '',
        role: userInStore.role || UserRoles.TECHNICAL,
        shift_id: userInStore.shift_id || (userInStore.shift && userInStore.shift.id) || '',
        receive_alerts: !!userInStore.receive_alerts,
        receive_push: !!userInStore.receive_push,
        receive_email: !!userInStore.receive_email,
        receive_whatsapp: !!userInStore.receive_whatsapp,
      }
    } else {
      const data = await UserResource.show(id)
      initialValues.value = {
        name: data.name,
        email: data.email,
        phone: data.phone_number || '',
        role: data.role || UserRoles.TECHNICAL,
        shift_id: data.shift_id || (data.shift && data.shift.id) || '',
        receive_alerts: !!data.receive_alerts,
        receive_push: !!data.receive_push,
        receive_email: !!data.receive_email,
        receive_whatsapp: !!data.receive_whatsapp,
      }
    }
  } catch (error) {
    console.error(error)
  } finally {
    isFetchingUser.value = false
  }
}

async function onSubmit(values: any, { setContext }: any) {
  // Configura a meta do yup na hora da validacao se necessario (já passado via prop meta)
  isLoading.value = true

  try {
    if (isEdit.value && props.userId) {
      const payload: IUpdateProfileParams = {
        id: props.userId,
        name: values.name,
        email: values.email,
        role: values.role,
        shift_id: values.shift_id,
        receive_alerts: !!values.receive_alerts,
        receive_push: !!values.receive_push,
        receive_email: !!values.receive_email,
        receive_whatsapp: !!values.receive_whatsapp,
      }
      
      if (values.phone) {
        payload.phone_number = values.phone
      }

      await userStore.updateCompanyUser(payload)
      useSuccessStore().setSuccess(['Usuário atualizado com sucesso.'])
    } else {
      const payload: ICreateUser = {
        name: values.name,
        email: values.email,
        role: values.role,
        password: values.password,
        password_confirmation: values.password_confirmation,
        shift_id: values.shift_id,
        receive_alerts: !!values.receive_alerts,
        receive_push: !!values.receive_push,
        receive_email: !!values.receive_email,
        receive_whatsapp: !!values.receive_whatsapp,
      }
      
      if (values.phone) {
        payload.phone_number = values.phone
      }

      await UserResource.createCompanyUser(payload)
      useSuccessStore().setSuccess(['Usuário criado com sucesso.'])
    }
    emit('saved')
    cancel()
  } catch (error: any) {
    // Tratar erro, exibir mensagem etc.
  } finally {
    isLoading.value = false
  }
}

function cancel() {
  emit('close')
}
</script>
